import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { 
  Target, 
  Play, 
  Pause, 
  Settings, 
  Plus, 
  UserPlus, 
  MessageSquare, 
  Calendar,
  TrendingUp,
  Users,
  AlertTriangle,
  CheckCircle
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface AutomationRulesProps {
  isConnected: boolean;
}

interface AutomationRule {
  id: string;
  name: string;
  type: 'connection_requests' | 'follow_up' | 'content_engagement' | 'lead_nurturing';
  status: 'active' | 'paused' | 'draft';
  trigger: string;
  action: string;
  conditions: string[];
  stats: {
    triggered: number;
    successful: number;
    rate: number;
  };
  createdAt: Date;
}

export const AutomationRules = ({ isConnected }: AutomationRulesProps) => {
  const [showNewRule, setShowNewRule] = useState(false);
  const [newRuleType, setNewRuleType] = useState("");
  const { toast } = useToast();

  // Mock automation rules
  const [automationRules, setAutomationRules] = useState<AutomationRule[]>([
    {
      id: '1',
      name: 'Auto Connect with Software Engineers',
      type: 'connection_requests',
      status: 'active',
      trigger: 'Profile viewed: Software Engineer',
      action: 'Send connection request with personalized message',
      conditions: ['Title contains "Software Engineer"', 'Location: San Francisco Bay Area', 'Company size: 100+ employees'],
      stats: { triggered: 45, successful: 32, rate: 71.1 },
      createdAt: new Date('2024-01-15')
    },
    {
      id: '2',
      name: 'Follow-up on Unanswered InMails',
      type: 'follow_up',
      status: 'active',
      trigger: 'InMail sent 7 days ago, no response',
      action: 'Send follow-up message',
      conditions: ['Original message sent > 7 days ago', 'No reply received', 'Message opened'],
      stats: { triggered: 23, successful: 14, rate: 60.9 },
      createdAt: new Date('2024-01-10')
    },
    {
      id: '3',
      name: 'Engage with Post Comments',
      type: 'content_engagement',
      status: 'paused',
      trigger: 'New comment on my post',
      action: 'Like comment and send thank you message',
      conditions: ['Comment is positive sentiment', 'From 1st degree connection', 'Not a bot account'],
      stats: { triggered: 67, successful: 61, rate: 91.0 },
      createdAt: new Date('2024-01-05')
    }
  ]);

  const ruleTypes = [
    {
      id: 'connection_requests',
      name: 'Connection Requests',
      description: 'Automatically send connection requests based on criteria',
      icon: UserPlus,
      color: 'text-blue-600'
    },
    {
      id: 'follow_up',
      name: 'Follow-up Messages',
      description: 'Send follow-up messages to prospects',
      icon: MessageSquare,
      color: 'text-purple-600'
    },
    {
      id: 'content_engagement',
      name: 'Content Engagement',
      description: 'Engage with posts and comments automatically',
      icon: TrendingUp,
      color: 'text-green-600'
    },
    {
      id: 'lead_nurturing',
      name: 'Lead Nurturing',
      description: 'Nurture leads through automated sequences',
      icon: Users,
      color: 'text-orange-600'
    }
  ];

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-100 text-green-800 border-green-200">Active</Badge>;
      case 'paused':
        return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">Paused</Badge>;
      case 'draft':
        return <Badge variant="outline">Draft</Badge>;
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <Play className="h-4 w-4 text-green-600" />;
      case 'paused':
        return <Pause className="h-4 w-4 text-yellow-600" />;
      case 'draft':
        return <Settings className="h-4 w-4 text-gray-600" />;
      default:
        return <Settings className="h-4 w-4 text-gray-600" />;
    }
  };

  const toggleRuleStatus = (ruleId: string) => {
    setAutomationRules(prev => prev.map(rule => {
      if (rule.id === ruleId) {
        const newStatus = rule.status === 'active' ? 'paused' : 'active';
        toast({
          title: `Rule ${newStatus === 'active' ? 'Activated' : 'Paused'}`,
          description: `${rule.name} has been ${newStatus === 'active' ? 'activated' : 'paused'}.`,
        });
        return { ...rule, status: newStatus as 'active' | 'paused' | 'draft' };
      }
      return rule;
    }));
  };

  if (!isConnected) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <div className="flex flex-col items-center gap-4">
            <Target className="h-12 w-12 text-muted-foreground" />
            <div>
              <h3 className="text-lg font-semibold">Connect LinkedIn First</h3>
              <p className="text-muted-foreground">Connect your LinkedIn account to set up automation</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Automation Rules</h2>
          <p className="text-muted-foreground">Set up automated LinkedIn actions and workflows</p>
        </div>
        <Dialog open={showNewRule} onOpenChange={setShowNewRule}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              New Rule
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Create New Automation Rule</DialogTitle>
              <DialogDescription>
                Set up automated actions to streamline your LinkedIn activities
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6">
              {/* Rule Type Selection */}
              <div className="space-y-3">
                <Label>Choose Automation Type</Label>
                <div className="grid grid-cols-2 gap-3">
                  {ruleTypes.map((type) => {
                    const IconComponent = type.icon;
                    return (
                      <Card 
                        key={type.id}
                        className={`cursor-pointer transition-colors ${
                          newRuleType === type.id ? 'ring-2 ring-primary' : 'hover:bg-muted/50'
                        }`}
                        onClick={() => setNewRuleType(type.id)}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <IconComponent className={`h-6 w-6 ${type.color}`} />
                            <div>
                              <h4 className="font-medium text-sm">{type.name}</h4>
                              <p className="text-xs text-muted-foreground">{type.description}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>

              {/* Rule Configuration (shown when type is selected) */}
              {newRuleType && (
                <div className="space-y-4 border-t pt-4">
                  <div className="space-y-2">
                    <Label>Rule Name</Label>
                    <Input placeholder="Give your automation rule a descriptive name..." />
                  </div>

                  <div className="space-y-2">
                    <Label>Trigger Conditions</Label>
                    <Textarea 
                      placeholder="Define when this rule should trigger..."
                      className="min-h-20"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Action to Take</Label>
                    <Select>
                      <SelectTrigger>
                        <SelectValue placeholder="Select action..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="send_connection">Send Connection Request</SelectItem>
                        <SelectItem value="send_message">Send Message</SelectItem>
                        <SelectItem value="like_post">Like Post</SelectItem>
                        <SelectItem value="comment_post">Comment on Post</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Message Template (if applicable)</Label>
                    <Textarea 
                      placeholder="Hi {name}, I noticed you work in {industry}..."
                      className="min-h-20"
                    />
                  </div>

                  <div className="space-y-4">
                    <Label>Safety Settings</Label>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium">Daily Limit</p>
                          <p className="text-xs text-muted-foreground">Maximum actions per day</p>
                        </div>
                        <Input type="number" defaultValue="10" className="w-20" />
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium">Delay Between Actions</p>
                          <p className="text-xs text-muted-foreground">Wait time between actions (minutes)</p>
                        </div>
                        <Input type="number" defaultValue="30" className="w-20" />
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium">Weekend Activity</p>
                          <p className="text-xs text-muted-foreground">Run automation on weekends</p>
                        </div>
                        <Switch />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowNewRule(false)}>
                  Cancel
                </Button>
                <Button variant="outline">
                  Save as Draft
                </Button>
                <Button disabled={!newRuleType}>
                  Create & Activate
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Important Notice */}
      <Card className="border-yellow-200 bg-yellow-50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-yellow-900">LinkedIn Automation Guidelines</h4>
              <p className="text-sm text-yellow-800 mt-1">
                Please ensure your automation complies with LinkedIn's terms of service. Keep daily limits reasonable 
                and always personalize your messages to maintain authenticity.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <p className="text-2xl font-bold">3</p>
              <p className="text-sm text-muted-foreground">Active Rules</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <p className="text-2xl font-bold">135</p>
              <p className="text-sm text-muted-foreground">Actions This Week</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <p className="text-2xl font-bold">74%</p>
              <p className="text-sm text-muted-foreground">Success Rate</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <p className="text-2xl font-bold">87</p>
              <p className="text-sm text-muted-foreground">New Connections</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Automation Rules List */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Your Automation Rules</h3>
        
        {automationRules.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <Target className="h-8 w-8 mx-auto mb-3 text-muted-foreground" />
              <p className="text-muted-foreground">No automation rules created yet</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {automationRules.map((rule) => (
              <Card key={rule.id}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-3">
                        {getStatusIcon(rule.status)}
                        <h4 className="font-medium">{rule.name}</h4>
                        {getStatusBadge(rule.status)}
                      </div>
                      
                      <div className="text-sm text-muted-foreground">
                        <p><strong>Trigger:</strong> {rule.trigger}</p>
                        <p><strong>Action:</strong> {rule.action}</p>
                      </div>
                      
                      <div className="flex flex-wrap gap-1">
                        {rule.conditions.map((condition, index) => (
                          <Badge key={index} variant="outline" className="text-xs">
                            {condition}
                          </Badge>
                        ))}
                      </div>
                      
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span>Triggered: {rule.stats.triggered}</span>
                        <span>Successful: {rule.stats.successful}</span>
                        <span className="text-green-600">Success Rate: {rule.stats.rate}%</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => toggleRuleStatus(rule.id)}
                      >
                        {rule.status === 'active' ? (
                          <>
                            <Pause className="h-4 w-4 mr-2" />
                            Pause
                          </>
                        ) : (
                          <>
                            <Play className="h-4 w-4 mr-2" />
                            Activate
                          </>
                        )}
                      </Button>
                      <Button variant="ghost" size="sm">
                        <Settings className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Best Practices */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-600" />
            Automation Best Practices
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="text-sm space-y-2 text-muted-foreground">
            <li>• Keep daily limits conservative (10-20 actions per day max)</li>
            <li>• Always personalize connection requests and messages</li>
            <li>• Monitor your automation performance regularly</li>
            <li>• Pause rules if response rates drop significantly</li>
            <li>• Add random delays between actions to appear more human</li>
            <li>• Respect LinkedIn's terms of service and user privacy</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};